import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type { CardFormat } from '../components/taskCard.logic';
import { addDays, deviceToday, formatClock, formatLongDay, formatShortDay } from '../utils/plannerDates';

export interface PlannerFormat extends CardFormat {
  locale: string;
  clock: (minute: number) => string;
  longDay: (day: string) => string;
}

export function formatDuration(minutes: number, t: TFunction): string {
  if (minutes < 60) return t('planner:duration.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hourText = t('planner:duration.hours', { count: hours });
  return rest === 0 ? hourText : t('planner:duration.hoursAndMinutes', { hours: hourText, minutes: rest });
}

export function usePlannerFormat(): PlannerFormat {
  const { t, i18n } = useTranslation('planner');
  const locale = i18n.language;

  return useMemo(() => {
    const day = (value: string): string => {
      const today = deviceToday();
      if (value === today) return t('dates.today');
      if (value === addDays(today, 1)) return t('dates.tomorrow');
      if (value === addDays(today, -1)) return t('dates.yesterday');
      return formatShortDay(value, locale);
    };
    const clock = (minute: number): string => formatClock(minute, locale);
    return {
      locale,
      day,
      clock,
      longDay: (value: string) => formatLongDay(value, locale),
      when: (value: string, minute: number | null) =>
        minute === null ? day(value) : t('dates.dayAtTime', { day: day(value), time: clock(minute) }),
      duration: (minutes: number) => formatDuration(minutes, t),
    };
  }, [t, locale]);
}
